import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useCallback, useEffect } from "react";
import {
  ChevronLeft,
  Edit,
  Calendar,
  Phone,
  Mail,
  Instagram,
  Cake,
  History,
  FileText,
  FileText as FileIcon,
  CircleDollarSign,
  Trash2,
  Download,
  Upload,
  ClipboardList,
  ShieldCheck,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ClienteForm } from "@/components/ClienteForm";
import { FORMAS_PAGAMENTO } from "@/components/financeiro/FinanceiroForm";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/clientes/$clienteId")({
  component: ClientePerfilPage,
});

const LABELS_SAUDE: Record<string, string> = {
  alergias: "Tem alergias?",
  tratamento: "Está em tratamento médico ou usa medicação contínua?",
  diabetes: "Tem diabetes?",
  epilepsia: "Tem epilepsia ou histórico de convulsão?",
  cardiopatia: "Possui cardiopatia?",
  hipertensao: "Possui hipo ou hipertensão arterial?",
  coagulacao: "Tem problema de coagulação ou hemofilia?",
  transmissivel: "Possui alguma doença transmissível pelo sangue?",
  pele_regiao: "Tem alguma doença de pele na região a ser tatuada?",
  queloide: "Tem tendência a queloide ou cicatriz hipertrófica?",
  gestante: "Está gestante ou amamentando?",
  fumante: "É fumante?",
  alcool_drogas: "Consumiu álcool ou drogas nas últimas 24h?",
  alimentou: "Alimentou-se bem nas últimas 4h?",
  pele_bronzeada: "Está com a pele bronzeada ou usando ácido no local?",
  reacao_anterior: "Já teve reação alérgica em tatuagem anterior?",
  anticoagulante: "Usa anticoagulante, aspirina ou anti-inflamatório recentemente?",
  desmaio: "Já teve desmaio ou mal-estar em procedimento com agulha?",
};


function ClientePerfilPage() {
  const { clienteId } = Route.useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [totalInvestido, setTotalInvestido] = useState(0);
  const [agendamentosCliente, setAgendamentosCliente] = useState<any[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [selectedAgendamento, setSelectedAgendamento] = useState<any>(null);
  const [fichas, setFichas] = useState<any[]>([]);
  const [isLoadingFicha, setIsLoadingFicha] = useState(true);
  const [selectedFicha, setSelectedFicha] = useState<any>(null);

  const fetchFicha = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("fichas_cliente")
        .select("*")
        .eq("cliente_id", clienteId)
        .order("criada_em", { ascending: false });

      if (error) throw error;
      setFichas(data || []);
    } catch (err) {
      console.error("Erro ao carregar ficha de anamnese:", err);
    } finally {
      setIsLoadingFicha(false);
    }
  }, [clienteId]);

  const fetchDocumentos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("documentos")
        .select("*")
        .eq("cliente_id", clienteId)
        .order("criado_em", { ascending: false });

      if (error) throw error;
      setDocumentos(data || []);
    } catch (err) {
      console.error("Erro ao carregar documentos:", err);
    }
  }, [clienteId]);

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Por favor, selecione apenas arquivos PDF");
      return;
    }

    try {
      setIsUploadingDoc(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      const filePath = `clientes/${clienteId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documentos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from("documentos")
        .insert({
          cliente_id: clienteId,
          nome_arquivo: file.name,
          url_arquivo: filePath,
          caminho_storage: filePath
        });

      if (dbError) throw dbError;

      toast.success("Documento enviado com sucesso!");
      fetchDocumentos();
    } catch (err: any) {
      console.error("Erro no upload do documento:", err);
      toast.error("Erro ao enviar documento: " + err.message);
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('documentos')
        .createSignedUrl(doc.caminho_storage, 60);

      if (error) throw error;

      window.open(data.signedUrl, '_blank');
    } catch (err: any) {
      console.error("Erro ao baixar documento:", err);
      toast.error("Erro ao baixar documento");
    }
  };

  const handleDocDelete = async (doc: any) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('documentos')
        .remove([doc.caminho_storage]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from("documentos")
        .delete()
        .eq("id", doc.id);

      if (dbError) throw dbError;

      toast.success("Documento removido!");
      fetchDocumentos();
    } catch (err: any) {
      console.error("Erro ao remover documento:", err);
      toast.error("Erro ao remover documento");
    }
  };

  const fetchClienteData = useCallback(async () => {
    try {
      setLoading(true);
      // Buscar dados básicos do cliente
      const { data: clienteData, error: clienteError } = await supabase
        .from("clientes")
        .select("*")
        .eq("id", clienteId)
        .single();

      if (clienteError) throw clienteError;
      setCliente(clienteData);

      // Buscar agendamentos para histórico e total investido
      const { data: agendamentosData, error: agendamentosError } = await supabase
        .from("agendamentos")
        .select("id, data, regiao_corpo, estilo, valor_total, valor_sinal, forma_pagamento, parcelas, status, horario_inicio, horario_fim, duracao, observacoes")
        .eq("cliente_id", clienteId)
        .neq("status", "Cancelado")
        .order("data", { ascending: false });

      if (agendamentosError) throw agendamentosError;

      setAgendamentosCliente(agendamentosData || []);
      const total = (agendamentosData || []).reduce((acc, curr) => acc + (Number(curr.valor_total) || 0), 0);
      setTotalInvestido(total);
    } catch (error) {
      console.error("Erro ao carregar dados do cliente:", error);
      toast.error("Erro ao carregar perfil do cliente");
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchClienteData();
    fetchDocumentos();
    fetchFicha();
  }, [fetchClienteData, fetchDocumentos, fetchFicha]);

  const handleUpdate = async (updatedData: any) => {
    try {
      const { error } = await supabase
        .from("clientes")
        .update(updatedData)
        .eq("id", clienteId);

      if (error) throw error;

      setCliente((prev: any) => ({ ...prev, ...updatedData }));
      toast.success("Cliente atualizado com sucesso!");
      setIsEditDialogOpen(false);
    } catch (err: any) {
      toast.error("Erro ao atualizar cliente: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="size-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!cliente) {
    return <div className="text-center py-20">Cliente não encontrado.</div>;
  }

  return (
    <div className="space-y-8 animate-in slide-in-from-right duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate({ to: "/clientes" })}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft size={24} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold text-foreground">{cliente.nome}</h1>
              <Badge className={cliente.status === "Ativo" ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-zinc-500/10 text-muted-foreground border-zinc-500/20"}>
                {cliente.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">ID do Cliente: #{clienteId}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-border text-foreground hover:bg-card">
                <Edit className="mr-2 size-4" />
                Editar
              </Button>
            </DialogTrigger>
            <DialogContent className="p-0 border-none bg-transparent max-w-2xl shadow-none">
              <ClienteForm 
                title="Editar Cliente"
                initialData={cliente}
                onSubmit={handleUpdate}
                onCancel={() => setIsEditDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
          
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CircleDollarSign className="text-primary size-5" />
                Resumo Financeiro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <span className="text-muted-foreground text-sm uppercase tracking-wider font-medium">Total Investido</span>
                <p className="text-3xl font-semibold text-primary">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalInvestido)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Informações de Contato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 text-foreground">
                <div className="size-8 rounded-lg bg-white/5 flex items-center justify-center text-muted-foreground">
                  <Phone size={16} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Telefone</p>
                  <p>{cliente.telefone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <div className="size-8 rounded-lg bg-white/5 flex items-center justify-center text-muted-foreground">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">E-mail</p>
                  <p>{cliente.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <div className="size-8 rounded-lg bg-white/5 flex items-center justify-center text-muted-foreground">
                  <Instagram size={16} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Instagram</p>
                  <p className="text-primary">{cliente.instagram}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <div className="size-8 rounded-lg bg-white/5 flex items-center justify-center text-muted-foreground">
                  <Cake size={16} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nascimento</p>
                  <p>{cliente.data_nascimento ? new Date(cliente.data_nascimento + "T12:00:00").toLocaleDateString('pt-BR') : "—"}</p>
                </div>
              </div>
              {(cliente.cpf || cliente.genero || cliente.estado_civil || cliente.endereco) && (
                <div className="pt-3 border-t border-border/50 space-y-2 text-sm">
                  {cliente.cpf && (
                    <div className="flex justify-between"><span className="text-muted-foreground">CPF</span><span className="text-foreground">{cliente.cpf}</span></div>
                  )}
                  {cliente.genero && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Gênero</span><span className="text-foreground">{cliente.genero}</span></div>
                  )}
                  {cliente.estado_civil && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Estado civil</span><span className="text-foreground">{cliente.estado_civil}</span></div>
                  )}
                  {cliente.endereco && (
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground shrink-0">Endereço</span><span className="text-foreground text-right">{cliente.endereco}</span></div>
                  )}
                  {(cliente.contato_emergencia_nome || cliente.contato_emergencia_telefone) && (
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground shrink-0">Emergência</span>
                      <span className="text-foreground text-right">
                        {cliente.contato_emergencia_nome}{cliente.contato_emergencia_nome && cliente.contato_emergencia_telefone ? " • " : ""}{cliente.contato_emergencia_telefone}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="text-primary size-5" />
                Histórico de Tatuagens
              </CardTitle>
            </CardHeader>
            <CardContent>
              {agendamentosCliente.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-6">Nenhum agendamento registrado.</p>
              ) : (
                <div className="space-y-3">
                  {agendamentosCliente.map((item: any) => {
                    const statusStyle: Record<string, string> = {
                      Confirmado: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
                      Concluído: "bg-sky-500/10 text-sky-600 border-sky-500/20",
                      Pendente: "bg-amber-500/10 text-amber-600 border-amber-500/20",
                      Reagendado: "bg-violet-500/10 text-violet-600 border-violet-500/20",
                      Cancelado: "bg-zinc-500/10 text-muted-foreground border-zinc-500/20",
                    };
                    const detalhe = [item.estilo, item.regiao_corpo].filter(Boolean).join(" • ");
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedAgendamento(item)}
                        className="flex items-start justify-between gap-3 p-4 rounded-xl bg-background border border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="size-11 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary shrink-0 leading-none">
                            <Calendar size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground">
                              {new Date(item.data + "T12:00:00").toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {item.horario_inicio && (
                                <span className="text-muted-foreground font-normal"> às {item.horario_inicio.slice(0, 5)}</span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {detalhe || "Sessão de tatuagem"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-foreground mb-1">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valor_total || 0)}
                          </p>
                          <Badge className={cn("text-[10px] font-semibold", statusStyle[item.status] || statusStyle.Cancelado)}>
                            {item.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="text-primary size-5" />
                Ficha de Anamnese
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingFicha ? (
                <p className="text-muted-foreground text-sm text-center py-6">Carregando ficha...</p>
              ) : fichas.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-6">
                  Este cliente ainda não preencheu a ficha de cadastro e anamnese.
                </p>
              ) : (
                <div className="space-y-3">
                  {fichas.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => setSelectedFicha(f)}
                      className="flex items-center justify-between gap-3 p-4 rounded-xl bg-background border border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="size-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <ClipboardList size={16} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">
                            {f.assinatura_data ? new Date(f.assinatura_data + "T12:00:00").toLocaleDateString('pt-BR') : "—"}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            Assinado por {f.assinatura_nome || "—"}
                          </p>
                        </div>
                      </div>
                      {f.eh_menor && (
                        <Badge className="text-[10px] font-semibold bg-amber-500/10 text-amber-600 border-amber-500/20 shrink-0">
                          Menor de idade
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="text-primary size-5" />
                Observações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {cliente.observacoes || "Nenhuma observação registrada."}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <FileIcon className="text-primary size-5" />
                Documentos
              </CardTitle>
              <div className="relative">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-border hover:bg-muted"
                  disabled={isUploadingDoc}
                  onClick={() => document.getElementById('doc-upload')?.click()}
                >
                  <Upload className="mr-2 size-4" />
                  {isUploadingDoc ? "Enviando..." : "Upload PDF"}
                </Button>
                <input 
                  id="doc-upload"
                  type="file" 
                  accept=".pdf"
                  className="hidden"
                  onChange={handleDocUpload}
                />
              </div>
            </CardHeader>
            <CardContent>
              {documentos.length > 0 ? (
                <div className="space-y-3">
                  {documentos.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border/50 group">
                      <div className="flex items-center gap-3">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <FileText size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm truncate max-w-[200px] md:max-w-md">{doc.nome_arquivo}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">{new Date(doc.criado_em).toLocaleDateString('pt-BR')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-foreground"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="size-8 text-muted-foreground hover:text-primary"
                          onClick={() => handleDocDelete(doc)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center border-2 border-dashed border-border rounded-xl">
                  <p className="text-muted-foreground text-sm">Nenhum documento PDF enviado.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={!!selectedAgendamento} onOpenChange={(open) => !open && setSelectedAgendamento(null)}>
        <DialogContent className="p-0 border-none bg-transparent max-w-md shadow-none">
          {selectedAgendamento && (
            <div className="bg-card text-foreground p-6 rounded-2xl border border-border w-full shadow-2xl space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Data e horário</p>
                <p className="text-lg font-semibold text-foreground">
                  {new Date(selectedAgendamento.data + "T12:00:00").toLocaleDateString('pt-BR')}
                  {selectedAgendamento.horario_inicio && ` às ${selectedAgendamento.horario_inicio.slice(0, 5)}`}
                  {selectedAgendamento.horario_fim && ` – ${selectedAgendamento.horario_fim.slice(0, 5)}`}
                </p>
              </div>

              {(selectedAgendamento.estilo || selectedAgendamento.regiao_corpo) && (
                <p className="text-sm text-muted-foreground">
                  {[selectedAgendamento.estilo, selectedAgendamento.regiao_corpo].filter(Boolean).join(" • ")}
                </p>
              )}

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm pt-2 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Duração</p>
                  <p className="text-foreground">{selectedAgendamento.duracao ? `${selectedAgendamento.duracao}h` : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Status</p>
                  <p className="text-foreground">{selectedAgendamento.status}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Valor total</p>
                  <p className="text-foreground font-semibold">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedAgendamento.valor_total || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Sinal pago</p>
                  <p className="text-foreground">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedAgendamento.valor_sinal || 0)}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Forma de pagamento</p>
                  <p className="text-foreground">
                    {FORMAS_PAGAMENTO.find(fp => fp.value === selectedAgendamento.forma_pagamento)?.label || "—"}
                    {selectedAgendamento.forma_pagamento === "credito" && selectedAgendamento.parcelas > 1 && ` em ${selectedAgendamento.parcelas}x`}
                  </p>
                </div>
              </div>

              {selectedAgendamento.observacoes && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Observações</p>
                  <p className="text-sm text-foreground leading-relaxed">{selectedAgendamento.observacoes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedFicha} onOpenChange={(open) => !open && setSelectedFicha(null)}>
        <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
          {selectedFicha && (
            <div className="bg-card text-foreground p-6 rounded-2xl border border-border w-full shadow-2xl space-y-5 max-h-[85vh] overflow-y-auto">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Ficha assinada em</p>
                <p className="text-lg font-semibold text-foreground">
                  {selectedFicha.assinatura_data ? new Date(selectedFicha.assinatura_data + "T12:00:00").toLocaleDateString('pt-BR') : "—"}
                </p>
              </div>

              {selectedFicha.eh_menor && (
                <div className="p-4 rounded-xl bg-background border border-border/50">
                  <p className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                    <UserCheck className="text-primary size-4" />
                    Autorizado por responsável legal
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-sm">
                    <div><span className="text-muted-foreground">Nome: </span><span className="text-foreground">{selectedFicha.responsavel_nome || "—"}</span></div>
                    <div><span className="text-muted-foreground">CPF: </span><span className="text-foreground">{selectedFicha.responsavel_cpf || "—"}</span></div>
                    <div><span className="text-muted-foreground">Nascimento: </span><span className="text-foreground">{selectedFicha.responsavel_nascimento ? new Date(selectedFicha.responsavel_nascimento + "T12:00:00").toLocaleDateString('pt-BR') : "—"}</span></div>
                    <div><span className="text-muted-foreground">Parentesco: </span><span className="text-foreground">{selectedFicha.responsavel_parentesco || "—"}</span></div>
                    <div className="md:col-span-2"><span className="text-muted-foreground">Endereço: </span><span className="text-foreground">{selectedFicha.responsavel_endereco || "—"}</span></div>
                    <div className="md:col-span-2"><span className="text-muted-foreground">Cidade/Estado: </span><span className="text-foreground">{selectedFicha.responsavel_cidade_estado || "—"}</span></div>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-2">Histórico de saúde</p>
                <div className="space-y-2">
                  {Object.entries(selectedFicha.respostas_anamnese || {}).map(([id, r]: [string, any]) => (
                    <div key={id} className="flex items-start justify-between gap-3 text-sm py-1.5 border-b border-border/40 last:border-0">
                      <span className="text-foreground">{LABELS_SAUDE[id] || id}</span>
                      <span className={`shrink-0 font-medium ${r?.valor === "sim" ? "text-primary" : "text-muted-foreground"}`}>
                        {r?.valor === "sim" ? `Sim${r.detalhe ? ` — ${r.detalhe}` : ""}` : "Não"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-sm">
                <div><span className="text-muted-foreground">Tipo sanguíneo: </span><span className="text-foreground">{selectedFicha.tipo_sanguineo || "—"}</span></div>
              </div>

              {selectedFicha.observacoes && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">Observações da ficha</p>
                  <p className="text-sm text-foreground leading-relaxed">{selectedFicha.observacoes}</p>
                </div>
              )}

              <div className="p-4 rounded-xl bg-background border border-border/50 space-y-2">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <ShieldCheck className="text-primary size-4" />
                  Consentimentos
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedFicha.aceite_uso_imagem ? "✓" : "✗"} Autorização de uso de imagem
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedFicha.aceite_termo_responsabilidade ? "✓" : "✗"} Termo de responsabilidade
                </p>
                <p className="text-sm text-foreground pt-1">
                  Assinado por <span className="font-medium">{selectedFicha.assinatura_nome || "—"}</span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
